import { create } from 'zustand';

/**
 * What the chat header needs to show the case as its one-line subtitle: the written line, and
 * whether the opening message that carries it is on screen.
 */
export type ClinicalCaseHeaderState = {
  conversationId: string;
  line: string;
  /** True while the opening message's bubble is visible in the list. */
  isOpeningVisible: boolean;
  /** False while earlier pages are unloaded, so there is no bubble to scroll back to. */
  canScrollToOpening: boolean;
};

type ClinicalCaseHeaderStore = {
  header: ClinicalCaseHeaderState | null;
  /** Which conversation's case summary is peeking over the thread, or null while none is. */
  peekId: string | null;
  /** Bumped by the peek to ask the chat list to scroll back to the opening message. */
  scrollRequest: { conversationId: string; nonce: number } | null;
  /** The reader's thumbs vote per conversation, shared so the box and the peek agree. */
  votes: Record<string, 'up' | 'down'>;
  setHeader: (header: ClinicalCaseHeaderState | null) => void;
  setPeekId: (peekId: string | null) => void;
  /** Closes the peek only if it belongs to this conversation, so leaving one chat spares another. */
  closePeek: (conversationId: string) => void;
  requestScrollToOpening: (conversationId: string) => void;
  setVote: (conversationId: string, vote: 'up' | 'down' | null) => void;
};

/**
 * The chat list publishes the case and whether its box is on screen, and the header, a sibling
 * rather than a child, reads it. In memory only: it describes the chat open right now.
 */
export const useClinicalCaseHeaderStore = create<ClinicalCaseHeaderStore>((set) => ({
  header: null,
  peekId: null,
  scrollRequest: null,
  votes: {},
  setHeader: (header) => set({ header }),
  setPeekId: (peekId) => set({ peekId }),
  closePeek: (conversationId) =>
    set((state) => (state.peekId === conversationId ? { peekId: null } : state)),
  requestScrollToOpening: (conversationId) =>
    set({ scrollRequest: { conversationId, nonce: Date.now() } }),
  setVote: (conversationId, vote) =>
    set((state) => {
      const { [conversationId]: _previous, ...rest } = state.votes;

      return { votes: vote ? { ...rest, [conversationId]: vote } : rest };
    }),
}));

/** The line for the header, or undefined while the opening message is in view or there is none. */
export function selectHeaderCaseLine(
  state: ClinicalCaseHeaderState | null,
  conversationId: string | undefined
): string | undefined {
  if (!state || !conversationId || state.conversationId !== conversationId) {
    return undefined;
  }

  return state.isOpeningVisible ? undefined : state.line;
}

type Frame = { top: number; bottom: number };

/** Least height of the box, in points, that counts as on screen rather than a clipped edge. */
const MIN_VISIBLE_BOX_HEIGHT = 16;

/**
 * Whether the case box itself is on screen. The opening message can be far taller than the
 * screen, so measuring the whole message says "in view" while the box at its top is cut off.
 * `topInset` is the floating header's clearance, which hides the top of the list.
 */
export function isCaseBoxInView(box: Frame, viewport: Frame, topInset: number): boolean {
  const visibleHeight =
    Math.min(box.bottom, viewport.bottom) - Math.max(box.top, viewport.top + topInset);

  return visibleHeight >= Math.min(MIN_VISIBLE_BOX_HEIGHT, box.bottom - box.top);
}

/**
 * Whether any of the opening message's bubble is on screen. The recommended layout puts the case
 * row at the bottom of the bubble, so the header line, jump chip and peek wait until the whole
 * bubble has scrolled away, not just the row. Reading the top of a long message must not bring
 * them in over it. `topInset` is the floating header's clearance, which hides the top of the list.
 */
export function isOpeningBubbleInView(bubble: Frame, viewport: Frame, topInset: number): boolean {
  const visibleHeight =
    Math.min(bubble.bottom, viewport.bottom) - Math.max(bubble.top, viewport.top + topInset);

  return visibleHeight > 0;
}

type MeasurableNode = {
  measureInWindow: (
    callback: (x: number, y: number, width: number, height: number) => void
  ) => void;
};

type DomMeasurableNode = Pick<Element, 'getBoundingClientRect'>;

const caseBoxNodes = new Map<string, MeasurableNode>();

const caseBoxListeners = new Map<string, Set<() => void>>();

function notifyCaseBox(conversationId: string) {
  caseBoxListeners.get(conversationId)?.forEach((listener) => listener());
}

/**
 * On web the box is a plain `div`, which has `getBoundingClientRect` but not React Native's
 * `measureInWindow`, so it is wrapped to answer the same question.
 */
function toMeasurable(node: MeasurableNode | DomMeasurableNode): MeasurableNode {
  if ('measureInWindow' in node) {
    return node;
  }

  return {
    measureInWindow: (callback) => {
      const { left, top, width, height } = node.getBoundingClientRect();

      callback(left, top, width, height);
    },
  };
}

/** The case box's view, registered by the box itself so the chat list can measure it. */
export function registerCaseBox(
  conversationId: string,
  node: MeasurableNode | DomMeasurableNode | null
) {
  if (node) {
    caseBoxNodes.set(conversationId, toMeasurable(node));
  } else {
    caseBoxNodes.delete(conversationId);
  }

  notifyCaseBox(conversationId);
}

/** Tells the chat list the box moved or resized, so visibility is measured again. */
export function notifyCaseBoxLayout(conversationId: string) {
  notifyCaseBox(conversationId);
}

export function getCaseBox(conversationId: string): MeasurableNode | undefined {
  return caseBoxNodes.get(conversationId);
}

export function subscribeToCaseBox(conversationId: string, listener: () => void): () => void {
  const listeners = caseBoxListeners.get(conversationId) ?? new Set<() => void>();

  listeners.add(listener);

  caseBoxListeners.set(conversationId, listeners);

  return () => {
    listeners.delete(listener);
  };
}
